import { useState, useEffect, useMemo } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import PropTypes from "prop-types";

import { AuthProvider, useAuth } from "contexts/AuthContext";
import { getRoutes, getSidenavRoutes, loadDepartmentRoutes, loadMinistryRoutes } from "routes";
import { userHasRouteAccess } from "utils/modulePermissions";

import chmeetings from "theme/chmeetings";
import DashboardShell from "shell/DashboardShell";

// --- ProtectedRoute ----------------------------------------------------------

function ProtectedRoute({ children, roles, routeKey }) {
  const { user, isLoading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (isLoading) return <Box sx={{ p: 4 }}>Loading…</Box>;
  if (!isAuthenticated)
    return <Navigate to="/authentication/sign-in" state={{ from: location }} replace />;
  if (!userHasRouteAccess(user, routeKey, roles)) return <Navigate to="/not-authorized" replace />;

  return children;
}

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  roles: PropTypes.arrayOf(PropTypes.string),
  routeKey: PropTypes.string,
};

// --- AppContent --------------------------------------------------------------

function AppContent() {
  const { church, isLoading, user, isAuthenticated } = useAuth();
  const [allAppRoutes, setAllAppRoutes] = useState(getRoutes());

  useEffect(() => {
    if (!church?.id) return;
    (async () => {
      try {
        await Promise.all([loadDepartmentRoutes(church.id), loadMinistryRoutes(church.id)]);
        setAllAppRoutes(getRoutes());
      } catch (e) {
        console.error("Failed to load department/ministry routes:", e);
      }
    })();
  }, [church?.id]);

  const filterSidenavRoutesByRole = (routes) => {
    if (!user) return [];
    return routes
      .filter((route) => route.type !== "hidden")
      .flatMap((route) => {
        if (route.collapse) {
          const filteredCollapse = route.collapse.filter((subRoute) =>
            userHasRouteAccess(user, subRoute.key, subRoute.roles)
          );
          return filteredCollapse.length > 0 ? { ...route, collapse: filteredCollapse } : null;
        }
        if (userHasRouteAccess(user, route.key, route.roles)) return route;
        return null;
      })
      .filter(Boolean);
  };

  const sidenavRoutes = useMemo(() => getSidenavRoutes(), [allAppRoutes]);
  const filteredSidenavRoutes = useMemo(
    () => filterSidenavRoutesByRole(sidenavRoutes),
    [sidenavRoutes, user]
  );

  const getRoutesJSX = (routes) =>
    routes
      .flatMap((route) => {
        if (route.collapse) return getRoutesJSX(route.collapse);
        if (route.route) {
          if (route.protected) {
            return (
              <Route
                key={route.key}
                path={route.route}
                element={
                  <DashboardShell routes={filteredSidenavRoutes}>
                    <ProtectedRoute roles={route.roles} routeKey={route.key}>
                      {route.component}
                    </ProtectedRoute>
                  </DashboardShell>
                }
              />
            );
          }
          return <Route key={route.key} path={route.route} element={route.component} />;
        }
        return null;
      })
      .filter(Boolean);

  const allRoutesJSX = useMemo(
    () => getRoutesJSX(allAppRoutes),
    [allAppRoutes, filteredSidenavRoutes]
  );

  if (isLoading) return <Box sx={{ p: 4 }}>Loading…</Box>;

  return (
    <Routes>
      {allRoutesJSX}
      <Route
        path="*"
        element={
          <Navigate to={isAuthenticated ? "/dashboard" : "/authentication/sign-in"} replace />
        }
      />
      <Route path="/not-authorized" element={<Box sx={{ p: 4 }}>Unauthorized Access</Box>} />
    </Routes>
  );
}

// --- App ---------------------------------------------------------------------

export default function App() {
  return (
    <ThemeProvider theme={chmeetings}>
      <CssBaseline />
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}
